"use client";

import { motion } from "framer-motion";

/** Glass pill group with a sliding gradient highlight — used on the /work pages. */
export function FilterPills<T extends string>({
  label,
  layoutId,
  options,
  active,
  onChange,
}: {
  label?: string;
  layoutId: string;
  /** `short` replaces `label` on phones — when any option has one, the pill spans the full width there */
  options: { value: T; label: string; short?: string; count?: number }[];
  active: T;
  onChange: (value: T) => void;
}) {
  const compact = options.some((o) => o.short);
  return (
    <div
      className={`flex flex-col items-center gap-3 3xl:gap-3.5 flex-shrink-0 ${
        compact ? "w-full sm:w-auto" : ""
      }`}
    >
      {label && (
        <span className="inline-flex items-center gap-2 text-[11px] lg:text-xs 3xl:text-sm font-semibold uppercase tracking-[0.2em] text-white/50">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: "linear-gradient(135deg, #E040A0, #FFB76C)" }}
          />
          {label}
        </span>
      )}
      <div
        className={`relative items-center gap-1 p-1.5 lg:p-2 3xl:p-2.5 rounded-full ${
          compact ? "flex w-full sm:inline-flex sm:w-auto" : "inline-flex"
        }`}
        style={{
          background: "rgba(14,14,22,0.72)",
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow:
            "0 20px 50px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        {options.map(({ value, label: text, short, count }) => {
          const on = active === value;
          return (
            <button
              key={value}
              onClick={() => onChange(value)}
              aria-pressed={on}
              className={`relative z-10 py-2 lg:py-2.5 3xl:py-3 rounded-full text-sm lg:text-base 3xl:text-lg font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                compact ? "flex-1 px-2 sm:flex-none sm:px-5 lg:px-6 3xl:px-8" : "px-5 lg:px-6 3xl:px-8"
              } ${on ? "text-white" : "text-white/55 hover:text-white"}`}
            >
              {on && (
                <motion.span
                  layoutId={layoutId}
                  className="absolute inset-0 rounded-full z-[-1]"
                  style={{
                    background: "linear-gradient(135deg, #FFB76C, #FF419D)",
                    boxShadow:
                      "0 10px 28px -10px rgba(255,65,157,0.75), inset 0 1px 0 rgba(255,255,255,0.3)",
                  }}
                  transition={{ type: "spring", stiffness: 420, damping: 36 }}
                />
              )}
              {short ? (
                <>
                  <span className="sm:hidden">{short}</span>
                  <span className="hidden sm:inline">{text}</span>
                </>
              ) : (
                text
              )}
              {count !== undefined && (
                <span
                  className={`${short ? "ml-1.5 sm:ml-2" : "ml-2"} text-[11px] lg:text-xs tabular-nums ${
                    on ? "text-white/80" : "text-white/35"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
